import { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";
import { sortDriversByCarNumber } from "../../lib/driverSorting.js";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { hasRole } from "../../auth/roles.js";
import { fetchPublicLicense } from "../../lib/publicDbApi.js";
import { createLicenseEvent } from "../../lib/rekoApi.js";

const RACE_COLUMNS = Array.from({ length: 12 }, (_, index) => `R${index + 1}`);

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function statusColor(level) {
    const key = String(level || "ok").toLowerCase();
    if (key === "season_ban") return "#ff6b6b";
    if (key === "race_ban") return "#ffb347";
    if (key === "qualy_ban") return "var(--accent, #e4fe3c)";
    return "#69fb90";
}

function classPillClass(className) {
    const key = String(className || "").toLowerCase();
    if (key === "pro") return "pill pill--pro";
    if (key === "int") return "pill pill--int";
    if (key === "am") return "pill pill--am";
    return "pill pill--gesamt";
}

function numberValue(value) {
    const n = Number(value || 0);
    return Number.isFinite(n) ? n : 0;
}

function licenseRaceStatusMeta(row, raceKey) {
    const status = row?.race_status?.[raceKey] || null;
    const state = String(status?.state || "").toLowerCase();
    const token = String(status?.token || "").toUpperCase();

    if (state === "ban" || token === "BAN") {
        return {
            className: "license-race-cell--ban",
            title: "BAN / Rennsperre",
        };
    }

    if (state === "started") {
        return {
            className: "license-race-cell--started",
            title: "Fahrer ist dieses Rennen gefahren",
        };
    }

    if (state === "not_started") {
        return {
            className: "license-race-cell--not-started",
            title: "Fahrer ist dieses Rennen nicht gefahren",
        };
    }

    return {
        className: "license-race-cell--unknown",
        title: "Keine Startinformation vorhanden",
    };
}

export default function Lizenz({
                                   seasonKey = "active",
                                   title = "Fahrerlizenz",
                                   subtitle = "Lizenzpunkte und automatische Strafstatus der GT3 Xperience.",
                               }) {
    const { user, loading: authLoading } = useAuth();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [selectedRaceId, setSelectedRaceId] = useState("");
    const [pointsDelta, setPointsDelta] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const currentSeasonKey = String(seasonKey || "active").trim() || "active";
    const isArchiveView = currentSeasonKey.toLowerCase() !== "active";

    const canManage = !authLoading && user && (
        hasRole(user, "admin") ||
        hasRole(user, "orga") ||
        hasRole(user, "reko_lead")
    );

    async function loadLicense() {
        try {
            setLoading(true);
            setError("");
            const res = await fetchPublicLicense({
                seriesKey: "gt3x",
                seasonKey: currentSeasonKey,
            });
            setData(res);
        } catch (err) {
            setError(prettyError(err));
            setData(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadLicense();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSeasonKey]);

    const rows = useMemo(() => {
        return sortDriversByCarNumber(Array.isArray(data?.rows) ? data.rows : []);
    }, [data]);

    const races = useMemo(() => Array.isArray(data?.races) ? data.races : [], [data]);

    const driverOptions = useMemo(() => rows.map((row) => ({
        value: String(row.season_driver_id),
        label: `${row.car_number ? `#${row.car_number} ` : ""}${row.driver_name}`,
    })), [rows]);

    const raceOptions = useMemo(() => races.map((race) => ({
        value: String(race.id),
        label: `${race.race_tag || `R${race.race_no}`} - ${race.track_name}`,
    })), [races]);

    const thresholds = data?.thresholds || {
        qualy_ban: 10,
        race_ban: 15,
        season_ban: 20,
    };

    async function submitManualEvent() {
        setError("");
        setSuccess("");

        const delta = Number(pointsDelta);
        if (!selectedDriverId) {
            setError("Bitte Fahrer auswählen.");
            return;
        }
        if (!Number.isFinite(delta) || delta === 0) {
            setError("Bitte Punkte ungleich 0 eintragen, z. B. -2 oder 3.");
            return;
        }
        if (!String(reason || "").trim()) {
            setError("Bitte einen Grund eintragen.");
            return;
        }

        try {
            setSaving(true);
            await createLicenseEvent({
                season_driver_id: Number(selectedDriverId),
                race_id: selectedRaceId ? Number(selectedRaceId) : null,
                points_delta: Math.trunc(delta),
                reason: reason.trim(),
            });
            setSuccess("Lizenzpunkte wurden gespeichert.");
            setPointsDelta("");
            setReason("");
            await loadLicense();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="rv-page license-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">GT3 Xperience</div>
                        <h1 className="rv-page-title">{title}</h1>
                        <p className="rv-page-subtitle">{subtitle}</p>
                    </div>
                </div>
            </section>

            <div className="card" style={{ display: "grid", gap: 8 }}>
                <h2 style={{ marginTop: 0 }}>Strafpunkte-Status</h2>
                <p className="muted">
                    Aktuelle Lizenzpunkte inklusive manuellem Abbau. Saisonpunkte bleiben als positive Saisonbelastung erhalten.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                    <span className="pill pill--am">OK &lt; {thresholds.qualy_ban}</span>
                    <span className="pill pill--int">Qualy-Sperre ab {thresholds.qualy_ban}</span>
                    <span className="pill pill--team">Rennsperre ab {thresholds.race_ban}</span>
                    <span className="pill pill--pro">Saisonausschluss ab {thresholds.season_ban}</span>
                </div>
            </div>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            {canManage && !isArchiveView && (
                <div className="card" style={{ display: "grid", gap: 12 }}>
                    <div>
                        <div className="rv-page-kicker">Admin / Orga / ReKo Lead</div>
                        <h2 style={{ margin: "4px 0 0" }}>Lizenzpunkte manuell buchen</h2>
                        <p className="muted" style={{ marginTop: 6 }}>
                            Für Abbau negative Punkte eintragen, z. B. -2. Diese Buchung ändert keine Meisterschaftspunkte.
                        </p>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                            gap: 10,
                        }}
                    >
                        <label style={{ display: "grid", gap: 6 }}>
                            <span>Fahrer</span>
                            <CustomSelect
                                value={selectedDriverId}
                                onChange={setSelectedDriverId}
                                options={driverOptions}
                                allowEmpty
                            />
                        </label>

                        <label style={{ display: "grid", gap: 6 }}>
                            <span>Rennen optional</span>
                            <CustomSelect
                                value={selectedRaceId}
                                onChange={setSelectedRaceId}
                                options={raceOptions}
                                allowEmpty
                            />
                        </label>

                        <label style={{ display: "grid", gap: 6 }}>
                            <span>Punkte</span>
                            <input
                                className="input"
                                type="number"
                                step="1"
                                value={pointsDelta}
                                onChange={(event) => setPointsDelta(event.target.value)}
                                placeholder="z. B. -2 oder 3"
                            />
                        </label>

                        <label style={{ display: "grid", gap: 6 }}>
                            <span>Grund</span>
                            <input
                                className="input"
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                                placeholder="z. B. Strafpunkte abgebaut"
                            />
                        </label>
                    </div>

                    <div>
                        <button className="btn btn--accent" type="button" onClick={submitManualEvent} disabled={saving || loading}>
                            Lizenzpunkte speichern
                        </button>
                    </div>
                </div>
            )}

            {loading && (
                <div className="card">
                    <p>Lade Fahrerlizenz…</p>
                </div>
            )}

            {!loading && !error && (
                <div className="license-race-legend">
                    <span className="license-race-legend__item">
                        <span className="license-race-legend__swatch license-race-legend__swatch--started" />
                        Gefahren
                    </span>
                    <span className="license-race-legend__item">
                        <span className="license-race-legend__swatch license-race-legend__swatch--not-started" />
                        Nicht gefahren
                    </span>
                    <span className="license-race-legend__item">
                        <span className="license-race-legend__swatch license-race-legend__swatch--ban" />
                        BAN
                    </span>
                </div>
            )}

            {!loading && !error && (
                <div className="fancyTable">
                    <table className="tbl tbl-body-everett penalties-table">
                        <thead>
                        <tr>
                            <th className="align-left">Fahrer</th>
                            <th>Aktuell</th>
                            {RACE_COLUMNS.map((raceKey) => (
                                <th key={raceKey}>{raceKey}</th>
                            ))}
                            <th>Saisonpunkte</th>
                            <th>Strafe</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.length ? rows.map((row) => {
                            const current = numberValue(row.current_points);
                            const season = numberValue(row.season_points);
                            const level = row.penalty_level || "ok";
                            const color = statusColor(level);
                            const racePoints = row.race_points || {};

                            return (
                                <tr key={row.season_driver_id}>
                                    <td className="align-left">
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                            {row.car_number ? <strong>#{row.car_number}</strong> : null}
                                            <span>{row.driver_name}</span>
                                            {row.class_name ? <span className={classPillClass(row.class_name)}>{row.class_name}</span> : null}
                                        </span>
                                    </td>
                                    <td className="num" style={{ color, fontWeight: 800 }}>
                                        {current}
                                    </td>
                                    {RACE_COLUMNS.map((raceKey) => {
                                        const value = numberValue(racePoints[raceKey]);
                                        const statusMeta = licenseRaceStatusMeta(row, raceKey);

                                        let cellColor;
                                        if (value > 0) cellColor = "#ff8c8c";
                                        if (value < 0) cellColor = "#69fb90";

                                        return (
                                            <td
                                                key={raceKey}
                                                className={`num penalty-grid-cell license-race-cell ${statusMeta.className}`}
                                                style={{ color: cellColor }}
                                                title={statusMeta.title}
                                            >
                                                {value !== 0 ? value : ""}
                                            </td>
                                        );
                                    })}
                                    <td className="num" style={{ color: season > 0 ? "var(--accent, #e4fe3c)" : undefined, fontWeight: 700 }}>
                                        {season || ""}
                                    </td>
                                    <td style={{ color, fontWeight: row.penalty_label ? 800 : 400 }}>
                                        {row.penalty_label || ""}
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={RACE_COLUMNS.length + 4} className="align-left text-dim">
                                    Noch keine Fahrer oder Lizenzpunkte vorhanden.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}